import { For, Show, createMemo, type Component, type JSX } from 'solid-js';
import { Panel } from '~ui/panel';
import { Toolbar, ToolbarSection } from '~ui/toolbar';
import { Text } from '~ui/text';
import { NA, formatCurrency, formatPercent, todayISO } from '~/lib/format';
import { annualDistribution, holdings, instrumentYield, managementFee, mandateOf, price, returnOfCapitalShare, riskRating } from '~data';
import { BasisChart } from '~/features/basis-chart';
import { FundFacts } from '~/features/fund-facts';
import { FundPicker } from '~/features/fund-picker';
import { DistributionChart, distributionSummary } from '~/features/distribution-chart';
import { TaxHeatmap } from '~/features/tax-heatmap';
import { YearlyReturns } from '~/features/yearly-returns';
import { Treemap } from '~/features/treemap';
import { fundSymbolFor } from '~/data/look-through';
import { bucketsFor } from '~/stores/exposure-store';
import { drillInto, popTo, rootSymbol, selectedSymbol, setSelectedSymbol, trail } from '~/stores/explore-store';

// One fund, the whole screen.
//
// Explore answers "which fund", in a table built for scanning eighteen of them.
// This answers "what is this fund", and the two share a selection so switching
// tabs keeps whichever one you were looking at.
//
// A fixed grid rather than a scrolling column, because the whole argument for a
// full-screen fund view is seeing these together. Total return against the
// distribution history against what is inside it is one thought; stacked in a
// column it becomes three, separated by scrolling, and nobody holds the first in
// their head long enough to reach the third.
//
//   ┌────────┬──────────────────┬──────────┐
//   │ Fund   │  Price, cash     │  Sector  │
//   │ facts  │  and reinvested  ├──────────┤
//   ├────────┤                  │ Holdings │
//   │ By     ├──────────────────┼──────────┤
//   │ year   │  Distribution    │  Tax     │
//   └────────┴──────────────────┴──────────┘
//
// The two charts share a column so their x-axes line up: they are the same fund
// over the same span, and reading the rate against the price only works if a
// point in one sits above the same date in the other.

const ASOF = todayISO();

// A cell's title bar is optional. The price chart does not get one: it is the
// largest thing on the page, its own legend names what it draws, and a header
// reading "Price vs total return" over it was a caption on the obvious.
const Cell: Component<{ title?: string; children: JSX.Element; class?: string; right?: JSX.Element }> = (props) => (
    <div class={`flex min-h-0 min-w-0 flex-col bg-background ${props.class ?? ''}`}>
        <Show when={props.title}>
            <div class='flex h-row shrink-0 items-center justify-between gap-2 border-b px-2'>
                <Text weight='medium' class='truncate'>
                    {props.title}
                </Text>
                <Show when={props.right}>
                    <span class='shrink-0'>{props.right}</span>
                </Show>
            </div>
        </Show>
        {props.children}
    </div>
);

const FundPage: Component = () => {
    const symbol = () => selectedSymbol();

    // Resolved rather than raw, so a fund of funds shows the sectors it reaches
    // rather than the ETFs it holds — see look-through.ts.
    const sectors = createMemo(() => [...bucketsFor(symbol(), 'sector')].sort((a, b) => b.weight - a.weight));
    const positions = createMemo(() => holdings(symbol()));

    /**
     * The holdings as tiles, keyed so a click can find its way back to a fund.
     *
     * Labelled by ticker where there is one — a tile is a rectangle a few
     * characters wide and "Hamilton U.S. Equity YIELD MAXIMIZER ETF" is not
     * going in it. The name is still on the tooltip and in the readout below.
     */
    const tiles = createMemo(() =>
        positions().map((h) => {
            const inner = fundSymbolFor(h);
            const key = h.symbol ?? inner ?? h.name;
            // Cash and written-option lines: no ticker, and not a fund either.
            // Drawn muted rather than dropped — a levered fund whose borrowing
            // is missing from the picture reads as unlevered.
            const position = h.symbol != null || inner != null;
            return { key, label: h.symbol ?? inner ?? h.name, value: h.weight, share: h.weight, hint: h.name, muted: !position };
        }),
    );

    /** Tile key to the fund it opens, for the holdings that are funds. */
    const openable = createMemo(() => {
        const map = new Map<string, string>();
        for (const h of positions()) {
            const inner = fundSymbolFor(h);
            if (inner != null) map.set(h.symbol ?? inner ?? h.name, inner);
        }
        return map;
    });

    return (
        <div class='flex-1 min-w-0'>
            <Panel>
                <Toolbar>
                    {/* One control rather than a button per fund. Shows the fund
                        on screen and opens a searchable list — see fund-picker.
                        Reads `rootSymbol` so drilling into a holding does not
                        make the picker claim you picked that fund. */}
                    <ToolbarSection class='min-w-0 flex-1'>
                        <FundPicker symbol={rootSymbol()} onSelect={setSelectedSymbol} />
                    </ToolbarSection>

                    <ToolbarSection class='min-w-0'>
                        {/* Only once you have gone somewhere. Clicking a holding
                            opens it, and that fund is often not one of the
                            buttons above — the crumbs are the way back. */}
                        <Show when={trail().length > 1}>
                            <For each={trail()}>
                                {(crumb, i) => (
                                    <>
                                        <Show when={i() > 0}>
                                            <Text tone='muted' class='shrink-0 px-1'>
                                                ›
                                            </Text>
                                        </Show>
                                        <Show when={i() < trail().length - 1} fallback={<Text weight='medium'>{crumb}</Text>}>
                                            <button type='button' class='shrink-0 cursor-default rounded px-1 hover:bg-muted' title={`Back to ${crumb}`} onClick={() => popTo(i())}>
                                                <Text tone='muted'>{crumb}</Text>
                                            </button>
                                        </Show>
                                    </>
                                )}
                            </For>
                        </Show>
                    </ToolbarSection>
                </Toolbar>

                {/* Three columns, and the facts are one of them rather than a
                    strip inside the chart's cell. That is what lets them run the
                    full height and what puts the distribution chart on exactly
                    the width of the price chart above it — the two are the same
                    fund over the same span, and reading one against the other
                    only works if their x-axes line up.

                    Auto-placement still does the work: facts take the whole first
                    column, the chart takes two rows of the second, sector and
                    holdings fall into the third, and the last two land in the
                    only cells left. */}
                <div class='grid min-h-0 flex-1 grid-cols-[3fr_8fr_5fr] grid-rows-3 gap-px bg-border'>
                    {/* No title bar. The picker above already names the fund, and
                        every row here is labelled — a header reading "Fund" over
                        a column of fund facts is a row spent on nothing. */}
                    <div class='row-span-3 flex min-h-0 min-w-0 flex-col bg-background'>
                        <div class='min-h-0 flex-1 overflow-y-auto scrollbar-hide'>
                            <FundFacts symbol={symbol()} />
                        </div>
                        {/* Under the facts and sharing the column with them. Both
                            are lists of one fund read down rather than across,
                            and the year grid is the only other thing on the page
                            narrow enough to sit here. */}
                        <div class='flex min-h-0 flex-1 flex-col border-t'>
                            <YearlyReturns symbol={symbol()} />
                        </div>
                    </div>

                    <Cell class='row-span-2'>
                        <BasisChart symbol={symbol()} height='min-h-0 flex-1' />
                    </Cell>

                    <Cell title='Sector allocation'>
                        <Treemap
                            items={sectors().map((b) => ({ key: b.key, label: b.name, value: b.weight, share: b.weight }))}
                            formatValue={(v) => formatPercent(v, 1)}
                            empty='Not published, and not resolvable from holdings'
                        />
                    </Cell>

                    <Cell title='Holdings'>
                        <Treemap
                            items={tiles()}
                            formatValue={(v) => formatPercent(v, 1)}
                            empty='This fund publishes no holdings'
                            onSelect={(key) => {
                                const inner = openable().get(key);
                                if (inner != null) drillInto(inner);
                            }}
                        />
                    </Cell>

                    <Cell title='Distribution rate' right={<Text tone='muted'>{distributionSummary(symbol())}</Text>}>
                        <DistributionChart symbol={symbol()} height='min-h-0 flex-1' />
                    </Cell>

                    <Cell title='Tax character'>
                        <TaxHeatmap symbol={symbol()} />
                    </Cell>
                </div>
            </Panel>
        </div>
    );
};

export default FundPage;
